import { redirect } from 'react-router';
import {
  type LoaderFunctionArgs,
  type ShouldRevalidateFunction,
  type ClientLoaderFunctionArgs,
} from 'react-router';
import { eq, inArray, asc, and } from 'drizzle-orm';
import { eventFormsTable, eventTable, formsEntryTable } from '~/db/schema';
import { type Form, type OrganizationEvent, type UserProfile } from '~/types';
import { getRegisteredUsersForEvent } from '~/model/getRegisteredUsersForEvent';
import { createEvent404Response } from '~/utils/errorResponses';
import { isAuthenticated } from '~/utils/isAuthenticated';
import { getOrgSlug } from '~/utils/server/admin/getOrgSlug';
import { getAddonsNeedingPayment } from '~/model/getAddonsNeedingPayment';

export interface WrapperLoaderData {
  event: OrganizationEvent;
  currentUserProfile: UserProfile;
  userProfiles: UserProfile[];
  forms: Form[];
  isFormCompleted: boolean[];
  countFormsCompleted: number;
  countForms: number;
  hasPayment: boolean;
}

const cache = new Map<string, WrapperLoaderData>();

export function clearWrapperLoaderCache() {
  cache.clear();
}

function redirectToFirstFormIfNeeded(
  request: Request,
  params: any,
  resolvedOrgSlug: string,
  hasCustomDomain: boolean,
) {
  const { eventSlug, userProfileId } = params;

  const url = new URL(request.url);
  const prefix = hasCustomDomain ? '' : `/${resolvedOrgSlug}`;
  // Ensure we don't double slash if prefix is empty
  const basePath = `${prefix}/event/${eventSlug}/${userProfileId}`;

  const needsFormRedirect =
    url.pathname === basePath || url.pathname === `${basePath}/`;

  if (userProfileId !== 'payment' && needsFormRedirect) {
    return redirect(`${basePath}/0`);
  }
}

export const loader = async ({
  request,
  context,
  params,
}: LoaderFunctionArgs) => {
  return context.performance.loaderSpan('formPage.wrapper.loader', async () => {
    const user = await isAuthenticated(request, context, {
      failureRedirect: '/',
    });

    const orgSlug = getOrgSlug({ params, context });
    const { eventSlug, userProfileId } = params;

    if (!orgSlug) {
      throw new Error('orgSlug is required');
    }

    const redirectRes = redirectToFirstFormIfNeeded(
      request,
      params,
      orgSlug,
      Boolean(context.orgSlug),
    );

    if (redirectRes) {
      return redirectRes;
    }

    if (!eventSlug || !userProfileId) {
      throw new Error('Missing params');
    }

    const userProfileIdInt = parseInt(userProfileId);

    if (isNaN(userProfileIdInt)) {
      throw new Response('User not found', { status: 404 });
    }

    const event = await context.drizzle.query.eventTable.findFirst({
      where: eq(eventTable.slug, eventSlug),
    });

    if (!event) {
      throw createEvent404Response({ eventSlug });
    }

    const registeredUsers = await getRegisteredUsersForEvent({
      context,
      userId: user.id,
      eventId: event.id,
    });

    if (registeredUsers.length === 0) {
      const prefix = context.orgSlug ? '' : `/${orgSlug}`;
      throw redirect(`${prefix}/event/${eventSlug}`);
    }

    const userProfiles = registeredUsers.map(
      (registeredUser) => registeredUser.userProfile,
    );

    const currentUserProfile = userProfiles.find(
      (userProfile) => userProfile.id === userProfileIdInt,
    );

    if (!currentUserProfile) {
      throw new Response('User profile not found', { status: 404 });
    }

    const eventForms = await context.drizzle.query.eventFormsTable.findMany({
      where: eq(eventFormsTable.eventId, event.id),
      orderBy: asc(eventFormsTable.formOrder),
      with: {
        form: true,
      },
    });

    const addonsNeedingPayment = await getAddonsNeedingPayment({
      context,
      userId: user.id,
      eventId: event.id,
      userProfileIds: registeredUsers.map(
        (registeredUser) => registeredUser.userProfileId,
      ),
    });

    if (eventForms.length === 0) {
      throw new Response('Forms not found', { status: 404 });
    }

    const completedForms = await context.drizzle.query.formsEntryTable.findMany(
      {
        where: and(
          inArray(
            formsEntryTable.userProfileId,
            userProfiles.map((userProfile) => userProfile.id),
          ),
          inArray(
            formsEntryTable.formId,
            eventForms.map((eventForm: any) => eventForm.formId),
          ),
        ),
      },
    );

    return {
      event,
      currentUserProfile,
      userProfiles,
      forms: eventForms.map((eventForm: any) => eventForm.form),
      isFormCompleted: eventForms.map((eventForm: any) =>
        completedForms.some(
          (completedForm: any) =>
            completedForm.userProfileId === currentUserProfile.id &&
            completedForm.formId === eventForm.formId,
        ),
      ),
      countFormsCompleted: completedForms.length,
      countForms: eventForms.length * userProfiles.length,
      hasPayment:
        (event.cost !== null && event.cost > 0) ||
        addonsNeedingPayment.length > 0,
    } satisfies WrapperLoaderData;
  });
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentParams,
  nextParams,
  formData,
}) => {
  const sentValues = Boolean(formData?.get('values'));

  if (sentValues) {
    clearWrapperLoaderCache();
  }

  return sentValues || currentParams.userProfileId !== nextParams.userProfileId;
};

export function buildCacheKey(params: {
  orgSlug?: string;
  eventSlug?: string;
  userProfileId?: string;
}) {
  // We can't strictly enforce orgSlug presence in params if it comes from context,
  // but for caching key purposes we might want it.
  // However, clientLoader params might not have orgSlug if custom domain.
  // We should handle that.
  const p = params as any;
  // If orgSlug is missing, it's effectively a "global" or context-based one.
  // We can use 'default' or similar if missing, but better to just use what we have.
  // Wait, if two orgs have same eventSlug?
  // Custom domains are unique to orgs.
  // So eventSlug + userProfileId is unique enough PER DOMAIN.
  // But cache is global in browser? No, cache is per page load.
  // Actually, cache here is module level.
  // If user switches domains? Unlikely in SPA.
  // We can just use what's there.
  return `${p.orgSlug || '_'}/${p.eventSlug}/${p.userProfileId}`;
}

// Client loader logic
// We need to implement redirect logic on client too?
// The original used `redirectToFirstFormIfNeeded` which used `request.url`.
// We can reuse the logic but we need to know if it's custom domain to strip prefix.
// We can guess custom domain by checking if params.orgSlug is present.
// If params.orgSlug is present, it's not custom domain (in the route config sense).
// If params.orgSlug is undefined, it IS custom domain (or bug).

export const clientLoader = async ({
  request,
  params,
  serverLoader,
}: ClientLoaderFunctionArgs) => {
  // Infer hasCustomDomain from params.orgSlug presence
  const hasCustomDomain = !params.orgSlug;
  // But we need orgSlug string for non-custom domain prefix.
  // If params.orgSlug is undefined, we pass empty string or handled inside.
  const resolvedOrgSlug = params.orgSlug || ''; // Only used if !hasCustomDomain

  const redirectRes = redirectToFirstFormIfNeeded(
    request,
    params,
    resolvedOrgSlug,
    hasCustomDomain,
  );

  if (redirectRes) {
    return redirectRes;
  }

  const cacheKey = buildCacheKey(params);
  const cachedData = cache.get(cacheKey);

  if (cachedData) {
    return cachedData;
  }

  const loaderData = await serverLoader();

  // @ts-ignore
  cache.set(cacheKey, loaderData);

  return loaderData;
};

// @ts-ignore
clientLoader.hydrate = true;
